import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Prompt, useHistory, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faUndo, faSync } from '@fortawesome/free-solid-svg-icons';

import { exportReadQueueRoster, exportSyncQueueRoster } from '../../../services';
import { parseExtensionInput, QUICK_INPUT_LIMIT } from '../../../services/extensionRange';
import DualList from '../../Common/DualList';
import ConfirmModal from '../../Common/ConfirmModal';
import Loading from '../../Loading/LoadingModule';
import {
  addExts,
  applyQuickInput,
  buildSyncPayload,
  createDraft,
  hasInvalidPenalty,
  isDirty,
  isOrderedStrategy,
  isPenaltyValid,
  moveDown,
  moveUp,
  pausedAmong,
  removeExts,
  setPenalty,
  summarize,
  tagsFor,
} from './queueMembersDraft';

import '../../../libs/bulma.min.css';
import './QueuesList.css';
import './QueueMembersRoster.css';

const QUEUES_LIST_PATH = '/config/fila/lista';

export const MESSAGES = {
  saved: 'Membros salvos',
  saveFallback: 'Falha ao salvar membros',
  loadFallback: 'Não foi possível carregar os membros da fila.',
  drifted: 'A fila foi alterada por outra pessoa. Recarregue para ver o estado atual.',
  limit: `Máximo de ${QUICK_INPUT_LIMIT} ramais por inclusão`,
  leavePrompt: 'Há alterações não salvas. Sair mesmo assim?',
  orderOnly: 'Ordem dos membros alterada',
  invalidPenalty: 'Prioridade inválida',
};

const labelOf = (item) => (item.name ? `${item.ext} · ${item.name}` : item.ext);
const searchTextOf = (item) => `${item.ext} ${item.name || ''}`;
const keyOf = (item) => item.ext;

const saveErrorMessage = (result) => (result.code || result.status === 429 ? result.error : MESSAGES.saveFallback);

const quickFeedbackLines = (outcome) => {
  const lines = [`Adicionados: ${outcome.added.length}`];
  if (outcome.notTenant.length) lines.push(`Não são ramais do tenant: ${outcome.notTenant.join(', ')}`);
  if (outcome.alreadyIn.length) lines.push(`Já na fila: ${outcome.alreadyIn.join(', ')}`);
  if (outcome.invalid.length) lines.push(`Inválidos: ${outcome.invalid.join(', ')}`);
  return lines;
};

function QueueMembersRoster() {
  const history = useHistory();
  const location = useLocation();
  const queueName = useMemo(() => new URLSearchParams(location.search).get('fila'), [location.search]);

  const [loading, setLoading] = useState(true);
  const [roster, setRoster] = useState(null);
  const [draft, setDraft] = useState(null);
  const [listVersion, setListVersion] = useState(0);
  const [alert, setAlert] = useState(null);
  const [drifted, setDrifted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmSave, setConfirmSave] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const [quickText, setQuickText] = useState('');
  const [quickFeedback, setQuickFeedback] = useState([]);

  const resetFrom = useCallback((data) => {
    setRoster(data);
    setDraft(createDraft(data));
    setListVersion((version) => version + 1);
    setQuickFeedback([]);
    setDrifted(false);
  }, []);

  const load = useCallback(async () => {
    const result = await exportReadQueueRoster({ queueName });
    if (!result.success) {
      if (result.status === 404) {
        history.replace(QUEUES_LIST_PATH);
        return false;
      }
      setAlert({ type: 'danger', message: result.error || MESSAGES.loadFallback });
      setLoading(false);
      return false;
    }
    resetFrom(result.data);
    setLoading(false);
    return true;
  }, [queueName, history, resetFrom]);

  useEffect(() => {
    if (!queueName) {
      history.replace(QUEUES_LIST_PATH);
      return;
    }
    load();
  }, [queueName, history, load]);

  const dirty = draft ? isDirty(draft) : false;
  const invalidPenalty = draft ? hasInvalidPenalty(draft) : false;
  const ordered = roster ? isOrderedStrategy(roster.strategy) : false;

  const removeNow = (keys) => setDraft((current) => removeExts(current, keys));

  const requestRemoval = (keys) => {
    const paused = pausedAmong(draft, keys);
    if (paused.length) {
      setPendingRemoval({ keys, paused });
      return;
    }
    removeNow(keys);
  };

  const confirmRemoval = () => {
    removeNow(pendingRemoval.keys);
    setPendingRemoval(null);
  };

  const discard = () => {
    resetFrom(roster);
    setAlert(null);
  };

  const reload = async () => {
    setAlert(null);
    await load();
  };

  const addQuick = () => {
    const parsed = parseExtensionInput(quickText);
    if (parsed.limitExceeded) {
      setQuickFeedback([MESSAGES.limit]);
      return;
    }
    const outcome = applyQuickInput(draft, parsed);
    setDraft(outcome.draft);
    setQuickFeedback(quickFeedbackLines(outcome));
    if (outcome.added.length) setQuickText('');
  };

  const save = async () => {
    setSaving(true);
    const result = await exportSyncQueueRoster(buildSyncPayload(draft, { queueName: roster.queueName, etag: roster.etag }));
    setSaving(false);
    setConfirmSave(false);
    if (result.success) {
      setAlert({ type: 'success', message: MESSAGES.saved });
      await load();
      return;
    }
    if (result.code === 'MEMBERS_DRIFTED') {
      setDrifted(true);
      return;
    }
    setAlert({ type: 'danger', message: saveErrorMessage(result) });
  };

  const renderTags = (item, side) => tagsFor(draft, item, side).map((tag) => (
    <span key={tag} className={`tag is-light queue-roster-tag queue-roster-tag-${tag === 'Removido' || tag === 'Ramal inexistente' ? 'danger' : 'info'}`}>{tag}</span>
  ));

  const renderExtras = (item, side) => {
    if (side !== 'left') return null;
    const valid = isPenaltyValid(item.penalty);
    return (
      <span className="queue-roster-penalty">
        <input
          type="number"
          min="0"
          step="1"
          className={`input is-small queue-roster-penalty-input${valid ? '' : ' is-danger'}`}
          aria-label={`Prioridade ${item.ext}`}
          value={item.penalty}
          disabled={item.invalidEndpoint}
          onChange={(event) => setDraft((current) => setPenalty(current, item.ext, event.target.value))}
        />
        {!valid && <span className="queue-roster-penalty-error">{MESSAGES.invalidPenalty}</span>}
      </span>
    );
  };

  if (loading || !draft) {
    return (
      <div className="queue-roster-container">
        {alert ? <div className={`alert-message alert-${alert.type}`}><div className="alert-body">{alert.message}</div></div> : <Loading />}
      </div>
    );
  }

  const quickInput = (
    <div className="queue-roster-quick">
      <input
        type="text"
        className="input is-small"
        aria-label="Digitação rápida de ramais"
        placeholder="2000-2010, 2015, 2020"
        value={quickText}
        onChange={(event) => setQuickText(event.target.value)}
        onKeyDown={(event) => { if (event.key === 'Enter') addQuick(); }}
      />
      <button type="button" className="button is-small" onClick={addQuick} disabled={quickText.trim() === ''}>Adicionar</button>
      {quickFeedback.length > 0 && (
        <ul className="queue-roster-quick-feedback">
          {quickFeedback.map((line) => <li key={line}>{line}</li>)}
        </ul>
      )}
    </div>
  );

  return (
    <div className="queue-roster-container">
      <Prompt when={dirty && !saving} message={MESSAGES.leavePrompt} />
      <div className="queue-roster-header">
        <div>
          <h1 className="queue-roster-title">Membros da fila</h1>
          <p className="queue-roster-subtitle">{`${roster.queueName} · estratégia ${roster.strategy}`}</p>
        </div>
        <div className="queue-roster-actions">
          <button type="button" className="btn-queue-action btn-view-queue" onClick={discard} disabled={!dirty || saving}>
            <FontAwesomeIcon icon={faUndo} />
            <span>Descartar</span>
          </button>
          <button type="button" className="btn-submit" onClick={() => setConfirmSave(true)} disabled={!dirty || invalidPenalty || saving}>
            <FontAwesomeIcon icon={faSave} />
            <span>{saving ? 'Salvando...' : 'Salvar'}</span>
          </button>
        </div>
      </div>

      {alert && (
        <div className="alerts-section">
          <div className={`alert-message alert-${alert.type}`}>
            <div className="alert-header">
              <span>{alert.type === 'success' ? 'Sucesso!' : 'Erro!'}</span>
              <button type="button" className="alert-close" aria-label="Fechar aviso" onClick={() => setAlert(null)}>×</button>
            </div>
            <div className="alert-body">{alert.message}</div>
          </div>
        </div>
      )}

      {drifted && (
        <div className="alerts-section">
          <div className="alert-message alert-danger queue-roster-drift">
            <div className="alert-body">
              {MESSAGES.drifted}
              <button type="button" className="button is-small queue-roster-reload" onClick={reload}>
                <FontAwesomeIcon icon={faSync} />
                <span>Recarregar</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <DualList
        key={listVersion}
        leftTitle="Na fila"
        rightTitle="Disponíveis"
        leftItems={draft.members}
        rightItems={draft.available}
        getKey={keyOf}
        getLabel={labelOf}
        getSearchText={searchTextOf}
        renderItemTags={renderTags}
        renderItemExtras={renderExtras}
        onMoveToLeft={(keys) => setDraft((current) => addExts(current, keys))}
        onMoveToRight={requestRemoval}
        reorder={{
          enabled: ordered,
          onMoveUp: (key) => setDraft((current) => moveUp(current, key)),
          onMoveDown: (key) => setDraft((current) => moveDown(current, key)),
        }}
        rightHeaderExtra={quickInput}
      />

      <ConfirmModal
        open={confirmSave}
        title="Salvar membros da fila"
        body={summarize(draft, roster.strategy) || MESSAGES.orderOnly}
        confirmLabel="Salvar"
        loadingLabel="Salvando..."
        loading={saving}
        onCancel={() => setConfirmSave(false)}
        onConfirm={save}
      />

      <ConfirmModal
        open={Boolean(pendingRemoval)}
        danger
        title="Remover membros pausados"
        body={pendingRemoval ? `Os ramais ${pendingRemoval.paused.join(', ')} estão pausados. Remover mesmo assim?` : ''}
        confirmLabel="Remover"
        onCancel={() => setPendingRemoval(null)}
        onConfirm={confirmRemoval}
      />
    </div>
  );
}

export default QueueMembersRoster;
